import { Form, Formik, FormikHelpers } from 'formik';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Permissions } from '../../../shared/auth/constants/permissions';
import defaultBiddingSettings from '../../../shared/defaultSettings/defaultBiddingSettings';
import defaultCompanySettings from '../../../shared/defaultSettings/defaultCompanySettings';
import { useCurrentUser, useHasPermission } from '../../Session';
import {
    DeviceSettingsType,
    GetBiddingSettingDocument,
    GetCompanySettingDocument,
    GetMessageTemplateSettingsDocument,
    SettingValueUnit,
    TemplateUseCases,
    useDisablePushMutation,
    useEnablePushMutation,
    useGetBiddingSettingQuery,
    useGetCompanySettingQuery,
    useGetMessageTemplateSettingsQuery,
    useGetSingleSignInTokenMutation,
    useToggleBiometricMutation,
    useUpdateBiddingSettingMutation,
    useUpdateCompanySettingMutation,
    useUpdateMessageTemplateSettingsMutation,
    useUploadCompanyFaviconMutation,
    useUploadCompanyLogoMutation,
} from '../../api';
import { useAlert } from '../../components/AlertProvider';
import { useFlutter } from '../../components/FlutterProvider';
import { useLoadingController } from '../../components/LoadingProvider';
import useValidator from '../../utilities/useValidator';
import validators from '../../utilities/validators';
import { SettingsHeader, SettingsBody } from './components';
import type { FormValues } from './shared';

type MessageTemplateFieldName =
    | 'upcomingBidTemplateUseCase'
    | 'biddingOpenedTemplateUseCase'
    | 'bidSubmissionTemplateUseCase'
    | 'highestBidSubmissionTemplateUseCase'
    | 'noChancesLeftTemplateUseCase'
    | 'bidAwardedTemplateUseCase'
    | 'bidAwardedForSalesConsultantTemplateUseCase'
    | 'otpTemplateUseCase'
    | 'userAccountRejectionTemplateUseCase'
    | 'userAccountApprovalTemplateUseCase';

const messageTemplateFields: Array<{ field: MessageTemplateFieldName; useCase: TemplateUseCases }> = [
    { field: 'upcomingBidTemplateUseCase', useCase: TemplateUseCases.UpcomingBid },
    { field: 'biddingOpenedTemplateUseCase', useCase: TemplateUseCases.BiddingOpened },
    { field: 'bidSubmissionTemplateUseCase', useCase: TemplateUseCases.BidSubmission },
    { field: 'highestBidSubmissionTemplateUseCase', useCase: TemplateUseCases.HighestBidSubmission },
    { field: 'noChancesLeftTemplateUseCase', useCase: TemplateUseCases.NoChancesLeft },
    { field: 'bidAwardedTemplateUseCase', useCase: TemplateUseCases.BidAwarded },
    {
        field: 'bidAwardedForSalesConsultantTemplateUseCase',
        useCase: TemplateUseCases.BidAwardedForSalesConsultant,
    },
    { field: 'otpTemplateUseCase', useCase: TemplateUseCases.Otp },
    { field: 'userAccountRejectionTemplateUseCase', useCase: TemplateUseCases.UserAccountRejection },
    { field: 'userAccountApprovalTemplateUseCase', useCase: TemplateUseCases.UserAccountApproval },
];

const BID_PERIOD_UNIT = 'minute';

const getSettingValueDefaultUnit = (isPercentageDefault: boolean) =>
    isPercentageDefault ? SettingValueUnit.Percentage : SettingValueUnit.Amount;

const isPercentageDefault = (unit: SettingValueUnit) => unit === SettingValueUnit.Percentage;

const Settings = () => {
    const { t } = useTranslation(['settingsPage']);
    const { show } = useAlert();
    const { deviceFingerPrint, checkBiometric, initializePush } = useFlutter();

    const user = useCurrentUser();
    const { deviceSettings } = user;
    const canUpdateCompanySettings = useHasPermission(Permissions.SETTINGS_UPDATE_COMPANY_SETTINGS);
    const canUpdateTemplateUseCases = useHasPermission(Permissions.SETTINGS_UPDATE_TEMPLATE_USE_CASES);

    const { data: messageTemplateSettingsData, loading: messageTemplateSettingsLoading } =
        useGetMessageTemplateSettingsQuery({
            skip: !canUpdateTemplateUseCases,
        });
    const { data: biddingSettingData, loading: biddingSettingLoading } = useGetBiddingSettingQuery();
    const { data: companySettingData, loading: companySettingLoading } = useGetCompanySettingQuery();

    const [toggleBiometric, { loading: toggleBiometricLoading }] = useToggleBiometricMutation();
    const [getSingleSignInToken, { loading: singleSignInTokenLoading }] = useGetSingleSignInTokenMutation();
    const [enablePush, { loading: enablePushLoading }] = useEnablePushMutation();
    const [disablePush, { loading: disablePushLoading }] = useDisablePushMutation();
    const [updateMessageTemplateSettings, { loading: updateMessageTemplateSettingsLoading }] =
        useUpdateMessageTemplateSettingsMutation();
    const [updateCompanySetting, { loading: updateCompanySettingLoading }] = useUpdateCompanySettingMutation();
    const [uploadCompanyFavicon, { loading: uploadCompanyFaviconLoading }] = useUploadCompanyFaviconMutation();
    const [uploadCompanyLogo, { loading: uploadCompanyLogoLoading }] = useUploadCompanyLogoMutation();
    const [updateBiddingSetting, { loading: updateBiddingSettingLoading }] = useUpdateBiddingSettingMutation();

    useLoadingController(
        messageTemplateSettingsLoading ||
            biddingSettingLoading ||
            companySettingLoading ||
            toggleBiometricLoading ||
            singleSignInTokenLoading ||
            enablePushLoading ||
            disablePushLoading ||
            updateMessageTemplateSettingsLoading ||
            updateCompanySettingLoading ||
            uploadCompanyFaviconLoading ||
            uploadCompanyLogoLoading ||
            updateBiddingSettingLoading
    );

    const useCases = useMemo(() => messageTemplateSettingsData?.messageTemplateSettings, [messageTemplateSettingsData]);
    const biddingSetting = biddingSettingData?.biddingSetting;

    const getTemplateSid = useCallback(
        (useCase: TemplateUseCases) =>
            useCases?.find(setting => setting.useCase === useCase)?.selectedTemplateSid ?? '',
        [useCases]
    );

    const formValidator = validators.compose(
        validators.only(
            () => canUpdateCompanySettings,
            validators.compose(
                validators.requiredString('companyName'),
                validators.requiredString('companyContactEmail'),
                validators.validEmail('companyContactEmail'),
                validators.requiredString('companyCurrency'),
                validators.requiredString('companyCurrencyCode'),
                validators.requiredString('companyTimezone'),
                validators.requiredString('companyCountryCode'),
                validators.validInteger('maxDealCount'),
                validators.validInteger('topDealCount'),
                validators.validInteger('maxSalesTransactionPhotos'),
                validators.validNonNegativeInteger('handlingFeesAmount'),
                validators.validNonNegativeNumber('handlingFeesPercentage'),
                validators.maxNumber('handlingFeesPercentage', 100),
                validators.validNonNegativeInteger('marginAmount'),
                validators.validNonNegativeNumber('marginPercentage'),
                validators.maxNumber('marginPercentage', 100),
                validators.validNonNegativeInteger('urgentBidPeriod'),
                validators.validNonNegativeInteger('notUrgentBidPeriod')
            )
        )
    );

    const validate = useValidator<FormValues>(formValidator);

    const initialValues = useMemo((): FormValues => {
        const find = (type: DeviceSettingsType) =>
            deviceSettings.some(setting => setting.type === type && setting.deviceFingerprint === deviceFingerPrint);
        const handlingFees = biddingSetting?.handlingFees ?? defaultBiddingSettings.handlingFees;
        const margin = biddingSetting?.margin ?? defaultBiddingSettings.margin;
        const urgentBidPeriod = biddingSetting?.urgentBidPeriod ?? defaultBiddingSettings.urgentBidPeriod;
        const notUrgentBidPeriod = biddingSetting?.notUrgentBidPeriod ?? defaultBiddingSettings.notUrgentBidPeriod;

        return {
            biometric: find(DeviceSettingsType.Biometric),
            pushNotification: find(DeviceSettingsType.Push),
            companyName: companySettingData?.companySetting.companyName ?? defaultCompanySettings.companyName,
            companyContactEmail: companySettingData?.companySetting.contactEmail ?? defaultCompanySettings.contactEmail,
            companyCurrency: companySettingData?.companySetting.currency ?? defaultCompanySettings.currency,
            companyCurrencyCode: companySettingData?.companySetting.currencyCode ?? defaultCompanySettings.currencyCode,
            companyTimezone: companySettingData?.companySetting.timezone ?? defaultCompanySettings.timezone,
            companyCountryCode: companySettingData?.companySetting.countryCode ?? defaultCompanySettings.countryCode,
            companyLogo: companySettingData?.companySetting.logoUrl ?? null,
            companyFavicon: companySettingData?.companySetting.faviconUrl ?? null,
            maxDealCount: biddingSetting?.maxDealCount ?? defaultBiddingSettings.maxDealCount,
            topDealCount: biddingSetting?.topDealCount ?? defaultBiddingSettings.topDealCount,
            maxSalesTransactionPhotos:
                biddingSetting?.maxSalesTransactionPhotos ?? defaultBiddingSettings.maxSalesTransactionPhotos,
            handlingFeesAmount: handlingFees.amount,
            handlingFeesPercentage: handlingFees.percentage,
            handlingFeesDefaultUnit: getSettingValueDefaultUnit(handlingFees.isPercentageDefault),
            marginAmount: margin.amount,
            marginPercentage: margin.percentage,
            marginDefaultUnit: getSettingValueDefaultUnit(margin.isPercentageDefault),
            urgentBidPeriod: urgentBidPeriod.value,
            notUrgentBidPeriod: notUrgentBidPeriod.value,
            enableUpcomingBidNotification:
                biddingSetting?.enableUpcomingBidNotification ?? defaultBiddingSettings.enableUpcomingBidNotification,
            biddingOpenedTemplateUseCase: getTemplateSid(TemplateUseCases.BiddingOpened),
            upcomingBidTemplateUseCase: getTemplateSid(TemplateUseCases.UpcomingBid),
            bidSubmissionTemplateUseCase: getTemplateSid(TemplateUseCases.BidSubmission),
            highestBidSubmissionTemplateUseCase: getTemplateSid(TemplateUseCases.HighestBidSubmission),
            noChancesLeftTemplateUseCase: getTemplateSid(TemplateUseCases.NoChancesLeft),
            bidAwardedTemplateUseCase: getTemplateSid(TemplateUseCases.BidAwarded),
            bidAwardedForSalesConsultantTemplateUseCase: getTemplateSid(TemplateUseCases.BidAwardedForSalesConsultant),
            otpTemplateUseCase: getTemplateSid(TemplateUseCases.Otp),
            userAccountRejectionTemplateUseCase: getTemplateSid(TemplateUseCases.UserAccountRejection),
            userAccountApprovalTemplateUseCase: getTemplateSid(TemplateUseCases.UserAccountApproval),
        };
    }, [
        biddingSetting,
        companySettingData?.companySetting.companyName,
        companySettingData?.companySetting.contactEmail,
        companySettingData?.companySetting.countryCode,
        companySettingData?.companySetting.currency,
        companySettingData?.companySetting.currencyCode,
        companySettingData?.companySetting.faviconUrl,
        companySettingData?.companySetting.logoUrl,
        companySettingData?.companySetting.timezone,
        deviceFingerPrint,
        deviceSettings,
        getTemplateSid,
    ]);

    const handleSubmit = useCallback(
        async (values: FormValues, { resetForm, setFieldValue }: FormikHelpers<FormValues>) => {
            const nextValues = { ...values };
            let hasSaved = false;

            if (values.biometric !== initialValues.biometric) {
                if (values.biometric) {
                    const biometricVerified = await checkBiometric();

                    if (!biometricVerified) {
                        nextValues.biometric = false;
                        await setFieldValue('biometric', false, false);
                    } else {
                        await toggleBiometric({ variables: { deviceFingerprint: deviceFingerPrint, state: true } });

                        const response = await getSingleSignInToken({
                            variables: { deviceFingerprint: deviceFingerPrint },
                        });
                        const token = response.data.getSingleSignInToken;

                        localStorage.setItem('singleSignIn', token);
                        hasSaved = true;
                    }
                } else {
                    await toggleBiometric({ variables: { deviceFingerprint: deviceFingerPrint, state: false } });
                    localStorage.removeItem('singleSignIn');
                    hasSaved = true;
                }
            }

            if (nextValues.pushNotification !== initialValues.pushNotification) {
                if (nextValues.pushNotification) {
                    const { registrationToken } = await initializePush();

                    if (!registrationToken) {
                        nextValues.pushNotification = false;
                        await setFieldValue('pushNotification', false, false);
                    } else {
                        await enablePush({ variables: { registrationToken, deviceFingerprint: deviceFingerPrint } });
                        hasSaved = true;
                    }
                } else {
                    await disablePush({ variables: { deviceFingerprint: deviceFingerPrint } });
                    hasSaved = true;
                }
            }

            // only Root user can edit message templates settings
            if (canUpdateTemplateUseCases) {
                const messageTemplateChanged = messageTemplateFields.some(
                    ({ field }) => values[field] !== initialValues[field]
                );

                if (messageTemplateChanged) {
                    await updateMessageTemplateSettings({
                        variables: {
                            useCaseSettings: messageTemplateFields.map(({ field, useCase }) => ({
                                useCase,
                                selectedTemplateSid: values[field] || null,
                            })),
                        },
                        refetchQueries: [{ query: GetMessageTemplateSettingsDocument }],
                    });

                    hasSaved = true;
                }
            }

            const companySettingChanged =
                values.companyName !== initialValues.companyName ||
                values.companyContactEmail !== initialValues.companyContactEmail ||
                values.companyCurrency !== initialValues.companyCurrency ||
                values.companyCurrencyCode !== initialValues.companyCurrencyCode ||
                values.companyTimezone !== initialValues.companyTimezone ||
                values.companyCountryCode !== initialValues.companyCountryCode;

            if (canUpdateCompanySettings) {
                if (companySettingChanged) {
                    await updateCompanySetting({
                        variables: {
                            fields: {
                                companyName: values.companyName,
                                contactEmail: values.companyContactEmail,
                                currency: values.companyCurrency,
                                currencyCode: values.companyCurrencyCode,
                                timezone: values.companyTimezone,
                                countryCode: values.companyCountryCode,
                            },
                        },
                        refetchQueries: [{ query: GetCompanySettingDocument }],
                    });

                    hasSaved = true;
                }

                if (values.companyLogo instanceof File) {
                    const response = await uploadCompanyLogo({
                        variables: {
                            file: values.companyLogo,
                        },
                        refetchQueries: [{ query: GetCompanySettingDocument }],
                    });

                    nextValues.companyLogo = response.data?.uploadCompanyLogo.logoUrl ?? null;
                    hasSaved = true;
                }

                if (values.companyFavicon instanceof File) {
                    const response = await uploadCompanyFavicon({
                        variables: {
                            file: values.companyFavicon,
                        },
                        refetchQueries: [{ query: GetCompanySettingDocument }],
                    });

                    nextValues.companyFavicon = response.data?.uploadCompanyFavicon.faviconUrl ?? null;
                    hasSaved = true;
                }

                const biddingSettingChanged =
                    Number(values.maxDealCount) !== Number(initialValues.maxDealCount) ||
                    Number(values.topDealCount) !== Number(initialValues.topDealCount) ||
                    Number(values.maxSalesTransactionPhotos) !== Number(initialValues.maxSalesTransactionPhotos) ||
                    Number(values.handlingFeesAmount) !== Number(initialValues.handlingFeesAmount) ||
                    Number(values.handlingFeesPercentage) !== Number(initialValues.handlingFeesPercentage) ||
                    values.handlingFeesDefaultUnit !== initialValues.handlingFeesDefaultUnit ||
                    Number(values.marginAmount) !== Number(initialValues.marginAmount) ||
                    Number(values.marginPercentage) !== Number(initialValues.marginPercentage) ||
                    values.marginDefaultUnit !== initialValues.marginDefaultUnit ||
                    Number(values.urgentBidPeriod) !== Number(initialValues.urgentBidPeriod) ||
                    Number(values.notUrgentBidPeriod) !== Number(initialValues.notUrgentBidPeriod) ||
                    values.enableUpcomingBidNotification !== initialValues.enableUpcomingBidNotification;

                if (biddingSettingChanged) {
                    await updateBiddingSetting({
                        variables: {
                            fields: {
                                handlingFees: {
                                    amount: Number(values.handlingFeesAmount),
                                    percentage: Number(values.handlingFeesPercentage),
                                    isPercentageDefault: isPercentageDefault(values.handlingFeesDefaultUnit),
                                },
                                margin: {
                                    amount: Number(values.marginAmount),
                                    percentage: Number(values.marginPercentage),
                                    isPercentageDefault: isPercentageDefault(values.marginDefaultUnit),
                                },
                                urgentBidPeriod: {
                                    value: Number(values.urgentBidPeriod),
                                    unit: BID_PERIOD_UNIT,
                                },
                                notUrgentBidPeriod: {
                                    value: Number(values.notUrgentBidPeriod),
                                    unit: BID_PERIOD_UNIT,
                                },
                                enableUpcomingBidNotification: values.enableUpcomingBidNotification,
                                maxDealCount: Number(values.maxDealCount),
                                topDealCount: Number(values.topDealCount),
                                maxSalesTransactionPhotos: Number(values.maxSalesTransactionPhotos),
                            },
                        },
                        refetchQueries: [{ query: GetBiddingSettingDocument }],
                    });

                    hasSaved = true;
                }
            }

            resetForm({ values: nextValues });

            if (hasSaved) {
                show('success', t('settingsPage:alerts.saveSuccessful'));
            }
        },
        [
            canUpdateCompanySettings,
            canUpdateTemplateUseCases,
            checkBiometric,
            deviceFingerPrint,
            disablePush,
            enablePush,
            getSingleSignInToken,
            initialValues,
            initializePush,
            show,
            t,
            toggleBiometric,
            updateBiddingSetting,
            updateCompanySetting,
            updateMessageTemplateSettings,
            uploadCompanyFavicon,
            uploadCompanyLogo,
        ]
    );

    // Render Formik only after data exists
    // to prevent users from seeing the form values being populated
    // while initialValues are not set yet
    if (
        (canUpdateTemplateUseCases && !useCases) ||
        !companySettingData?.companySetting ||
        !biddingSettingData?.biddingSetting
    ) {
        return null;
    }

    return (
        <Formik
            key={deviceFingerPrint || 'none'}
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validate={validate}
        >
            {() => (
                <Form>
                    <SettingsHeader />
                    <SettingsBody />
                </Form>
            )}
        </Formik>
    );
};

export default Settings;
